import { Multicall } from "ethereum-multicall";
import { ethers } from "ethers";
import { BASE18, BASE8, DEFAULT_ADDRESS, ONE_RAY } from "src/config/constants";
import BigNumber from "bignumber.js";
import { getAssetData } from "src/config/tokens";
import BEP20 from "src/assets/abis/Bep20Abi.json";
import TToken from "src/assets/abis/tokens/TToken.json";
import DebtToken from "src/assets/abis/tokens/DebtToken.json";
import AaveOracle from "src/assets/abis/poolMonitoring/AaveOracle.json";
import LendingPool from "src/assets/abis/poolMonitoring/LendingPool.json";
import IncentiveAbi from "src/assets/abis/poolMonitoring/IncentiveAbi.json";
import ProviderFactory from "src/assets/abis/poolMonitoring/ProviderFactory.json";
import InterestRateFactory from "src/assets/abis/poolMonitoring/InterestRateFactory.json";
import TravaPriceFetcher from "../trava-price-fetcher";

export default class PoolFetcher {
  constructor(addresses, provider, network) {
    this._addresses = addresses;
    this._provider = provider;
    this._network = network;

    this._multiCall = new Multicall({ ethersProvider: this._provider, tryAggregate: true });
    this._providerContract = new ethers.Contract(
      this._addresses.addressesProviderFactoryAddress,
      ProviderFactory,
      this._provider
    );
    this._trava_price_fetcher = new TravaPriceFetcher(this._provider, network);

    this.getOraclePrice = this.getOraclePrice.bind(this);
    this._decodeTokenData = this._decodeTokenData.bind(this);
    this._getTokenTotalData = this._getTokenTotalData.bind(this);
    this._getIncentiveData = this._getIncentiveData.bind(this);
    this._getRiskParam = this._getRiskParam.bind(this);
    this._getInterestRateStrategy = this._getInterestRateStrategy.bind(this);
    this._mergeFinalResult = this._mergeFinalResult.bind(this);
    this.getPoolTokenData = this.getPoolTokenData.bind(this);
    this.getPoolsAddressData = this.getPoolsAddressData.bind(this);
    this.getPoolsData = this.getPoolsData.bind(this);
  }

  async getOraclePrice(reserveAddressList) {
    let _dataCall = [];
    for (let address of reserveAddressList) {
      _dataCall.push({
        reference: address,
        contractAddress: this._addresses.ORACLE_ADDRESS,
        abi: AaveOracle,
        calls: [{ reference: "getAssetPrice", methodName: "getAssetPrice", methodParameters: [address] }],
      });
    }
    let _result = await this._multiCall.call(_dataCall);
    _result = _result.results;
    let result = {};
    for (let address of reserveAddressList) {
      const _data = _result[address].callsReturnContext[0];
      result[address.toLowerCase()] = {
        price: new BigNumber(_data.returnValues[0].hex).dividedBy(BASE8).toNumber(),
      };
    }
    return result;
  }

  _decodeTokenData(reserveAddressList, tokenData) {
    const result = {};
    for (var address of reserveAddressList) {
      const bep20Data = tokenData[`bep20_${address}`].callsReturnContext;
      const reserveData = tokenData[`data_${address}`].callsReturnContext;
      result[address.toLowerCase()] = {
        address: address.toLowerCase(),
        decimal: bep20Data[0].returnValues[0],
        symbol: bep20Data[1].returnValues[0],
        name: bep20Data[2].returnValues[0],
        depositRate: new BigNumber(reserveData[0].returnValues[3].hex).dividedBy(ONE_RAY).toNumber(),
        borrowRate: new BigNumber(reserveData[0].returnValues[4].hex).dividedBy(ONE_RAY).toNumber(),
        tTokenAddress: reserveData[0].returnValues[6],
        debtTokenAddress: reserveData[0].returnValues[7],
        reserveInterestRateAddress: reserveData[0].returnValues[8],
      };
    }
    return result;
  }

  async _getTokenTotalData(reserveAddressList, decodeTokenData) {
    const tokenPriceList = await this.getOraclePrice(reserveAddressList);
    let _dataCall = [];
    reserveAddressList.forEach((address) => {
      const lowerAddress = address.toLowerCase();
      const _tokenData = decodeTokenData[lowerAddress];
      _dataCall.push({
        reference: `t_token_${address}`,
        contractAddress: _tokenData.tTokenAddress,
        abi: TToken,
        calls: [{ reference: "t_totalSupply", methodName: "totalSupply" }],
      });
      _dataCall.push({
        reference: `d_token_${address}`,
        contractAddress: _tokenData.debtTokenAddress,
        abi: DebtToken,
        calls: [{ reference: "d_totalSupply", methodName: "totalSupply" }],
      });
    });
    const _result = (await this._multiCall.call(_dataCall)).results;
    let result = {};
    reserveAddressList.forEach((address) => {
      const lowerAddress = address.toLowerCase();
      const _tokenData = decodeTokenData[lowerAddress];
      const _price = tokenPriceList[lowerAddress].price;
      const _tData = _result[`t_token_${address}`].callsReturnContext[0].returnValues[0];
      const _dData = _result[`d_token_${address}`].callsReturnContext[0].returnValues[0];
      const _decimal = new BigNumber("10").pow(_tokenData.decimal);
      const totalSupply = new BigNumber(_tData.hex).dividedBy(_decimal) * _price;
      const totalBorrow = new BigNumber(_dData.hex).dividedBy(_decimal) * _price;
      result[lowerAddress] = { totalSupply, totalBorrow, price: _price };
    });
    return result;
  }

  async _getIncentiveData(reserveAddressList, decodeTokenData, totalTokenData) {
    let _dataCall = [];
    for (let address of reserveAddressList) {
      const lowerAddress = address.toLowerCase();
      const _tokenData = decodeTokenData[lowerAddress];
      _dataCall.push({
        reference: address,
        contractAddress: this._addresses.INCENTIVES_ADDRESS,
        abi: IncentiveAbi,
        calls: [
          { reference: "t_getAssetData", methodName: "getAssetData", methodParameters: [_tokenData.tTokenAddress] },
          { reference: "d_getAssetData", methodName: "getAssetData", methodParameters: [_tokenData.debtTokenAddress] },
        ],
      });
    }
    let _result = await this._multiCall.call(_dataCall);
    _result = _result.results;
    let result = {};
    const travaPrice = await this._trava_price_fetcher.getTravaPrice();
    for (let address of reserveAddressList) {
      const lowerAddress = address.toLowerCase();
      const totalSupply = totalTokenData[lowerAddress].totalSupply;
      const totalBorrow = totalTokenData[lowerAddress].totalBorrow;
      const _data = _result[address].callsReturnContext;
      const rawTTokenEPS = new BigNumber(_data[0].returnValues[1].hex).dividedBy(BASE18).toNumber();
      const rawDTokenEPS = new BigNumber(_data[1].returnValues[1].hex).dividedBy(BASE18).toNumber();
      result[address.toLowerCase()] = {
        rawTTokenEPS: rawTTokenEPS,
        rawDTokenEPS: rawDTokenEPS,
        tTokenEPS: (rawTTokenEPS * 365 * 24 * 60 * 60 * travaPrice) / totalSupply,
        dTokenEPS: (rawDTokenEPS * 365 * 24 * 60 * 60 * travaPrice) / totalBorrow,
      };
    }
    return result;
  }

  async _getRiskParam(poolAddress, reserveAddressList) {
    let _dataCall = [];
    for (let address of reserveAddressList) {
      _dataCall.push({
        reference: address,
        contractAddress: poolAddress,
        abi: LendingPool,
        calls: [{ reference: "getConfiguration", methodName: "getConfiguration", methodParameters: [address] }],
      });
    }
    let _result = await this._multiCall.call(_dataCall);
    _result = _result.results;
    let result = {};
    for (let address of reserveAddressList) {
      const _data = _result[address].callsReturnContext[0].returnValues[0];
      let binaryAssetConfig = BigNumber(_data.hex).toString(2);
      if (binaryAssetConfig.length < 80) {
        binaryAssetConfig = "0".repeat(80 - binaryAssetConfig.length) + binaryAssetConfig;
      }
      const maxLTV = parseInt(binaryAssetConfig.slice(-15), 2) / 100 / 100;
      const liqThres = parseInt(binaryAssetConfig.slice(-31, -16), 2) / 100 / 100;
      const liqBonus = (parseInt(binaryAssetConfig.slice(-47, -32), 2) / 100 - 100) / 100;
      const reserveFactor = parseInt(binaryAssetConfig.slice(-79, -64), 2) / 100 / 100;
      const length = binaryAssetConfig.length;
      const isActive = +binaryAssetConfig[length - 57];
      const isFreezed = +binaryAssetConfig[length - 58];
      const enabledBorrow = +binaryAssetConfig[length - 59];
      const collateral = maxLTV > 0;
      result[address.toLowerCase()] = {
        maxLTV,
        liqThres,
        liqBonus,
        reserveFactor,
        collateral,
        isActive,
        isFreezed,
        enabledBorrow,
      };
    }
    return result;
  }

  async _getInterestRateStrategy(reserveAddressList, decodeTokenData) {
    let _dataCall = [];
    for (let address of reserveAddressList) {
      const _tokenData = decodeTokenData[address.toLowerCase()];
      _dataCall.push({
        reference: address,
        contractAddress: _tokenData.reserveInterestRateAddress,
        abi: InterestRateFactory,
        calls: [
          { reference: "OPTIMAL_UTILIZATION_RATE", methodName: "OPTIMAL_UTILIZATION_RATE" },
          { reference: "variableRateSlope1", methodName: "variableRateSlope1" },
          { reference: "variableRateSlope2", methodName: "variableRateSlope2" },
          { reference: "baseVariableBorrowRate", methodName: "baseVariableBorrowRate" },
        ],
      });
    }
    const _result = await this._multiCall.call(_dataCall);
    const result = {};
    for (let address of reserveAddressList) {
      const _data = _result.results[address].callsReturnContext;
      result[address.toLowerCase()] = {
        utilizationOptimal: new BigNumber(_data[0].returnValues[0].hex).dividedBy(ONE_RAY).toNumber(),
        slope1: new BigNumber(_data[1].returnValues[0].hex).dividedBy(ONE_RAY).toNumber(),
        slope2: new BigNumber(_data[2].returnValues[0].hex).dividedBy(ONE_RAY).toNumber(),
        baseBorrowRate: new BigNumber(_data[3].returnValues[0].hex).dividedBy(ONE_RAY).toNumber(),
      };
    }
    return result;
  }

  _mergeFinalResult(
    reserveAddressList,
    decodeTokenData,
    totalTokenData,
    incentiveTokenData,
    riskData,
    interestRateData
  ) {
    let result = {};
    for (let address of reserveAddressList) {
      const lowAddress = address.toLowerCase();
      const _assetData = getAssetData(address, this._network);
      result[lowAddress] = {
        ...decodeTokenData[lowAddress],
        ...totalTokenData[lowAddress],
        ...incentiveTokenData[lowAddress],
        ...interestRateData[lowAddress],
        risk: riskData[lowAddress],
        image: _assetData.image,
        code: _assetData.symbol,
      };
    }
    return result;
  }

  async getPoolTokenData(poolAddress) {
    const lendingPoolContract = new ethers.Contract(poolAddress, LendingPool, this._provider);
    const reserveAddressList = await lendingPoolContract.getReservesList();
    if (reserveAddressList.length == 0) return undefined;
    let _dataCall = [];
    reserveAddressList.forEach((address) => {
      _dataCall.push({
        reference: `bep20_${address}`,
        contractAddress: address,
        abi: BEP20,
        calls: [
          { reference: "decimal", methodName: "decimals" },
          { reference: "symbol", methodName: "symbol" },
          { reference: "name", methodName: "name" },
        ],
      });
      _dataCall.push({
        reference: `data_${address}`,
        contractAddress: poolAddress,
        abi: LendingPool,
        calls: [{ reference: "getReserveData", methodName: "getReserveData", methodParameters: [address] }],
      });
    });
    const _tokenData = await this._multiCall.call(_dataCall);
    const tokenData = this._decodeTokenData(reserveAddressList, _tokenData.results);
    const totalTokenData = await this._getTokenTotalData(reserveAddressList, tokenData);
    const incentiveTokenData = await this._getIncentiveData(reserveAddressList, tokenData, totalTokenData);
    const riskData = await this._getRiskParam(poolAddress, reserveAddressList);
    const interestRateData = await this._getInterestRateStrategy(reserveAddressList, tokenData);
    return this._mergeFinalResult(
      reserveAddressList,
      tokenData,
      totalTokenData,
      incentiveTokenData,
      riskData,
      interestRateData
    );
  }

  async getPoolsAddressData() {
    const providerIdList = await this._providerContract.getAllPools();
    let _dataCall = [];
    for (let providerId of providerIdList) {
      const _providerId = BigNumber(providerId._hex).toFixed();
      _dataCall.push({
        reference: _providerId,
        contractAddress: this._addresses.addressesProviderFactoryAddress,
        abi: ProviderFactory,
        calls: [{ reference: "poolAddress", methodName: "getLendingPool", methodParameters: [Number(_providerId)] }],
      });
    }
    let _result = await this._multiCall.call(_dataCall);
    let result = [];
    for (let providerId of providerIdList) {
      const _providerId = BigNumber(providerId._hex).toFixed();
      const poolAddress = _result["results"][providerId].callsReturnContext[0].returnValues[0];
      if (poolAddress !== DEFAULT_ADDRESS) result.push({ providerId: _providerId, poolAddress: poolAddress });
    }
    return result;
  }

  async getPoolsData() {
    const addressData = await this.getPoolsAddressData();
    let result = {};
    for (let data of addressData) {
      const tokenData = await this.getPoolTokenData(data.poolAddress);
      if (tokenData)
        result[data.poolAddress.toLowerCase()] = {
          overview: { ...data, name: "Lending Pool" },
          tokens: tokenData,
        };
    }
    return result;
  }
}
